import resolveBreadcrumbLink from '@/lib/resolveBreadcrumbLink'
import { getLocaleFromPath, getSiteUrl, localizePath } from '@/lib/locales'
import { pickSignificantImageUrl } from '@/lib/ogImage'
import {
  CLINIC,
  CLINIC_ID,
  MEDICAL_SPECIALTIES,
  MEDICAL_SPECIALTY_ALIASES,
  PROCEDURE_TYPES,
  SITE_ORIGIN,
} from './constants'
import {
  compact,
  getBlockContent,
  pickImageUrl,
  stripHtml,
  toAbsoluteUrl,
  truncateText,
} from './utils'

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const OPENING_HOURS = WEEKDAYS.map((dayOfWeek) => ({
  '@type': 'OpeningHoursSpecification',
  dayOfWeek,
  opens: '08:00',
  closes: '22:00',
}))

const MEDICAL_SPECIALTIES_BY_LENGTH = [...MEDICAL_SPECIALTIES].sort((a, b) => b.length - a.length)

function toSpecialtyUrl(name) {
  return `https://schema.org/${name}`
}

function resolveMedicalSpecialty(direction) {
  const haystack = `${direction?.slug || ''} ${direction?.title || ''}`.toLowerCase()
  if (!haystack.trim()) {
    return null
  }

  const alias = MEDICAL_SPECIALTY_ALIASES.find(({ keywords }) =>
    keywords.some((keyword) => haystack.includes(keyword))
  )
  if (alias) {
    return toSpecialtyUrl(alias.specialty)
  }

  const compactHaystack = haystack.replace(/[^a-zа-яё0-9]+/gi, '')
  const specialty = MEDICAL_SPECIALTIES_BY_LENGTH.find((name) =>
    compactHaystack.includes(name.toLowerCase())
  )

  return specialty ? toSpecialtyUrl(specialty) : null
}

function buildMedicalSpecialties(directions = []) {
  const specialties = directions.map(resolveMedicalSpecialty).filter(Boolean)

  return [...new Set(specialties)]
}

function buildPhysicianSpecialties(specialist = {}) {
  const fromDirections = buildMedicalSpecialties(
    (specialist.services || []).map((service) => service?.direction).filter(Boolean)
  )
  if (fromDirections.length) {
    return fromDirections
  }

  const position = stripHtml(specialist.position || '')
  if (!position) {
    return []
  }

  const fromPosition = resolveMedicalSpecialty({ title: position, slug: position })
  return fromPosition ? [fromPosition] : [position]
}

export function buildMedicalClinic({ directions = [] } = {}) {
  const medicalSpecialty = buildMedicalSpecialties(directions)

  return compact({
    '@context': 'https://schema.org',
    '@type': 'MedicalClinic',
    '@id': CLINIC_ID,
    name: CLINIC.name,
    url: CLINIC.url,
    logo: CLINIC.logo,
    image: CLINIC.image,
    telephone: CLINIC.telephone,
    email: CLINIC.email,
    priceRange: CLINIC.priceRange,
    isAcceptingNewPatients: CLINIC.isAcceptingNewPatients,
    foundingDate: CLINIC.foundingDate,
    sameAs: CLINIC.sameAs,
    hasMap: CLINIC.hasMap,
    medicalSpecialty: medicalSpecialty.length ? medicalSpecialty : undefined,
    identifier: {
      '@type': 'PropertyValue',
      name: 'DHA License',
      value: CLINIC.dhaLicense,
    },
    address: {
      '@type': 'PostalAddress',
      ...CLINIC.address,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: CLINIC.geo.latitude,
      longitude: CLINIC.geo.longitude,
    },
    openingHoursSpecification: OPENING_HOURS,
  })
}

export function buildBreadcrumbList(items = [], { host } = {}) {
  if (!items || items.length < 2) {
    return null
  }

  const siteUrl = getSiteUrl(host)

  // Same items and same href/as resolution as the visible Breadcrumbs component.
  const list = items.map((item, index, arr) => {
    const isLast = index === arr.length - 1
    const { as } = resolveBreadcrumbLink(item)

    const entry = {
      '@type': 'ListItem',
      position: index + 1,
      name: stripHtml(item?.title || ''),
    }

    // Google: the last crumb must not have `item`.
    if (!isLast) {
      entry.item = toAbsoluteUrl(as, siteUrl)
    }

    return entry
  })

  return compact({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: list,
  })
}

export function buildFaqPage(questions = []) {
  const valid = (questions || []).filter((item) => item && item.question && item.answer)
  if (!valid.length) {
    return null
  }

  return compact({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: valid.map((item) => ({
      '@type': 'Question',
      name: stripHtml(item.question),
      acceptedAnswer: {
        '@type': 'Answer',
        text: stripHtml(item.answer),
      },
    })),
  })
}

const DUBAI_OFFSET_MS = 4 * 60 * 60 * 1000

function toSchemaDate(value) {
  if (!value) {
    return undefined
  }

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? String(value).slice(0, 10) : date.toISOString().slice(0, 10)
}

function formatDubaiDateTime(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return undefined
  }

  const dubai = new Date(date.getTime() + DUBAI_OFFSET_MS)
  return `${dubai.toISOString().slice(0, 19)}+04:00`
}

function toSchemaDateTime(value) {
  if (value == null || value === '') {
    return undefined
  }

  if (value instanceof Date || typeof value === 'number') {
    return formatDubaiDateTime(value)
  }

  let raw = String(value).trim()
  if (!raw) {
    return undefined
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return `${raw}T00:00:00+04:00`
  }

  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(raw)) {
    raw = raw.replace(' ', 'T')
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) {
      raw += ':00'
    }
    raw += '+04:00'
  }

  return formatDubaiDateTime(raw)
}

function pickModifiedDate(entity = {}) {
  return toSchemaDateTime(
    entity.dateModified || entity.date_modified || entity.updated_at || entity.modified_at || entity.last_reviewed
  )
}

function buildKnowsLanguage(value) {
  if (!value || typeof value !== 'string') {
    return undefined
  }

  const languages = value
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean)

  return languages.length ? languages : undefined
}

function buildSameAs(specialist = {}) {
  const urls = [
    specialist.doctify,
    specialist.linkedin,
    specialist.dha,
    specialist.instagram_link,
    specialist.fb_link,
  ]
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean)

  return urls.length ? [...new Set(urls)] : undefined
}

function resolveProcedureType(service = {}) {
  const type = service.procedure_type || service.direction?.procedure_type
  return PROCEDURE_TYPES.has(type) ? type : 'MedicalProcedure'
}

function getServiceUrl(service = {}, { siteUrl, locale, pageUrl } = {}) {
  return (
    pageUrl ||
    toAbsoluteUrl(
      localizePath(`/${service?.direction?.slug || ''}/${service?.slug || ''}`.replace(/\/+/g, '/'), locale),
      siteUrl
    )
  )
}

function getPhysicianSlug(value) {
  if (!value) {
    return null
  }

  if (typeof value === 'string') {
    return value.trim() || null
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const slug = getPhysicianSlug(item)
      if (slug) {
        return slug
      }
    }
    return null
  }

  return getPhysicianSlug(value.slug)
}

function toOfferPrice(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : undefined
  }

  if (typeof value === 'string') {
    const price = value.trim()
    return price || undefined
  }

  return undefined
}

function buildOffer(price, url) {
  const offerPrice = toOfferPrice(price)
  if (!offerPrice) {
    return undefined
  }

  return {
    '@type': 'Offer',
    priceCurrency: 'AED',
    price: offerPrice,
    availability: 'https://schema.org/InStock',
    url,
  }
}

function buildMedicalProcedure(service = {}, { siteUrl, locale, pageUrl } = {}) {
  const url = getServiceUrl(service, { siteUrl, locale, pageUrl })
  const firstBlockContent = getBlockContent(service.blocks?.[0])

  return compact({
    '@type': resolveProcedureType(service),
    '@id': `${url}#procedure`,
    name: service.title,
    howPerformed: firstBlockContent ? truncateText(firstBlockContent, 500) : undefined,
    preparation: service.preparation ? stripHtml(service.preparation) : undefined,
    followup: service.followup ? stripHtml(service.followup) : undefined,
    offers: buildOffer(service.price, url),
  })
}

function getRelatedPhysician(service = {}) {
  const candidates = [service.author, service.specialist, service.physician, service.reviewed_by]

  for (const value of candidates) {
    const person = Array.isArray(value) ? value.find(Boolean) : value
    if (getPhysicianSlug(person)) {
      return person
    }
  }

  return null
}

function buildLinkedPhysician(person, { siteUrl, locale } = {}) {
  const slug = getPhysicianSlug(person)
  if (!slug) {
    return null
  }

  const url = toAbsoluteUrl(localizePath(`/specialists/${slug}`, locale), siteUrl)
  const name =
    person && typeof person === 'object'
      ? [person.last_name, person.first_name, person.patronymic].filter(Boolean).join(' ').trim()
      : ''

  return compact({
    '@type': 'Physician',
    '@id': `${url}#physician`,
    name: name || undefined,
    url,
  })
}

export function buildServiceSchemas(service = {}, { host, asPath, pageUrl } = {}) {
  const siteUrl = getSiteUrl(host)
  const locale = getLocaleFromPath(asPath || pageUrl || '/')
  const url = getServiceUrl(service, { siteUrl, locale, pageUrl })
  const description = service?.meta?.description || service.description || 'ELEMENTAL Day Surgical Center'
  const lastReviewed = toSchemaDate(service.last_reviewed)
  const bannerUrl = pickSignificantImageUrl(service.banner)
  const physician = buildLinkedPhysician(getRelatedPhysician(service), { siteUrl, locale })
  const physicianRef = physician ? { '@id': physician['@id'] } : undefined
  const provider = physicianRef || { '@id': CLINIC_ID }
  const procedure = service.title
    ? buildMedicalProcedure(service, { siteUrl, locale, pageUrl })
    : null
  const procedureRef = procedure ? { '@id': procedure['@id'] } : undefined

  return compact({
    '@context': 'https://schema.org',
    '@graph': [
      compact({
        '@type': 'MedicalWebPage',
        '@id': `${url}#webpage`,
        name: service.title,
        description: stripHtml(description),
        url,
        image: bannerUrl ? toAbsoluteUrl(bannerUrl, siteUrl) : undefined,
        inLanguage: locale === 'ar' ? 'ar' : 'en',
        isPartOf: { '@id': CLINIC_ID },
        about: procedureRef,
        mainEntity: procedureRef,
        dateModified: pickModifiedDate(service),
        lastReviewed,
        reviewedBy: physicianRef,
        author: physicianRef,
        provider,
      }),
      procedure,
      physician,
    ].filter(Boolean),
  })
}

export function buildDirectionSchemas(direction = {}, { host, asPath, pageUrl } = {}) {
  const siteUrl = getSiteUrl(host)
  const locale = getLocaleFromPath(asPath || pageUrl || '/')
  const path = `/${direction.slug || ''}`
  const url = pageUrl || toAbsoluteUrl(localizePath(path, locale), siteUrl)
  const description = direction?.meta?.description || direction.short_content || direction.title
  const bannerUrl = pickSignificantImageUrl(direction.banner)

  return compact({
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    '@id': `${url}#webpage`,
    name: direction.h1 || direction.title,
    description: description ? stripHtml(description) : undefined,
    url,
    image: bannerUrl ? toAbsoluteUrl(bannerUrl, siteUrl) : undefined,
    inLanguage: locale === 'ar' ? 'ar' : 'en',
    isPartOf: { '@id': CLINIC_ID },
    dateModified: pickModifiedDate(direction),
    provider: { '@id': CLINIC_ID },
  })
}

export function buildPhysicianSchema(specialist = {}, { host, asPath, pageUrl } = {}) {
  const siteUrl = getSiteUrl(host)
  const locale = getLocaleFromPath(asPath || pageUrl || '/')
  const path = `/specialists/${specialist.slug || ''}`
  const url = pageUrl || toAbsoluteUrl(localizePath(path, locale), siteUrl)
  const name = [specialist.last_name, specialist.first_name, specialist.patronymic]
    .filter(Boolean)
    .join(' ')
    .trim()

  const specialties = buildPhysicianSpecialties(specialist)

  const availableService = (specialist.services || [])
    .map((service) => {
      const procedure = buildMedicalProcedure(service, { siteUrl, locale })
      return procedure?.['@id'] ? { '@id': procedure['@id'] } : undefined
    })
    .filter(Boolean)

  const image = pickImageUrl(specialist.image) || pickImageUrl(specialist.preview)
  const dateModified = toSchemaDateTime(specialist.dateModified)
  const knowsLanguage = buildKnowsLanguage(specialist.knowsLanguage)
  const sameAs = buildSameAs(specialist)

  const physicianId = `${url}#physician`

  return compact({
    '@context': 'https://schema.org',
    '@graph': [
      compact({
        '@type': 'MedicalWebPage',
        '@id': `${url}#webpage`,
        name,
        url,
        inLanguage: locale === 'ar' ? 'ar' : 'en',
        about: { '@id': physicianId },
        mainEntity: { '@id': physicianId },
        provider: { '@id': CLINIC_ID },
      }),
      compact({
        '@type': 'Physician',
        '@id': physicianId,
        name,
        description: specialist.qualification || undefined,
        url,
        dateModified,
        knowsLanguage,
        sameAs,
        image: image ? toAbsoluteUrl(image, siteUrl) : undefined,
        address: {
          '@type': 'PostalAddress',
          ...CLINIC.address,
        },
        parentOrganization: { '@id': CLINIC_ID },
        medicalSpecialty: specialties.length ? specialties : undefined,
        availableService: availableService.length ? availableService : undefined,
        identifier: specialist.license_number
          ? {
              '@type': 'PropertyValue',
              name: 'DHA License',
              value: specialist.license_number,
            }
          : undefined,
      }),
    ],
  })
}

export { CLINIC_ID, SITE_ORIGIN }
